import React, { useState, useMemo } from 'react';
import { Modal, Row, Col, Card, Tag } from 'antd';
import { useQuery } from 'react-query';
import { isNil, sumBy } from 'lodash';
import DataGrid from 'components/DataGrid/DataGrid';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import DF from 'DisplayFramework';
import { ACCList } from 'services/accounting';
import {
  getAccountingEntryByAccReference,
  GetAccountingEntryByAccReferenceParams,
} from 'services/accounting';
import { useModalState } from 'hooks/useModalState';
import UtinaCard from 'components/UtinaCard';
import { toCurrency } from 'utils/utils';
import GetLabel from 'DisplayFramework/components/GetLabel';

type AccountingEntryModalProps = {
  visible: boolean;
  onCancel: () => void;
  accountingReference: string;
};

type AccountingEntryProps = {
  accountingReference: string;
};

const AccountingEntryModal = ({
  accountingReference,
}: AccountingEntryProps) => {
  const { close, open, visible } = useModalState();
  return (
    <>
      <a
        onClick={(e) => {
          e.preventDefault();
          open();
        }}
      >
        {accountingReference}
      </a>
      {visible && (
        <AccountingEntry
          visible={visible}
          accountingReference={accountingReference}
          onCancel={close}
        />
      )}
    </>
  );
};

const AccountingEntry = (props: AccountingEntryModalProps) => {
  const { visible, onCancel, accountingReference } = props;

  const [searchParams, setSearchParams] =
    useState<GetAccountingEntryByAccReferenceParams>({
      accreference: accountingReference,
    });
  // eslint-disable-next-line no-unused-vars
  const [searchs, setsearchs] = useState(false);

  const { data: entries, isFetching } = useQuery(
    ['getAccountingEntryByAccReference', searchParams],
    () =>
      getAccountingEntryByAccReference(searchParams).then(
        (res) => res.list || []
      ),
    { refetchOnMount: true, enabled: !isNil(accountingReference) }
  );

  const columns = useMemo<DataGridColDef<ACCList>[]>(
    () => [
      {
        dataIndex: 'accountingreference',
        fixed: 'left',
        width: 200,
      },
      { dataIndex: 'accmodel', fixed: 'left' },
      { dataIndex: 'acclabel', width: 250 },
      { dataIndex: 'accsubreference', width: 150 },
      { dataIndex: 'accsublabel', width: 250 },
      { dataIndex: 'linelabel', width: 250 },
      { dataIndex: 'accattribute', width: 130 },
      { dataIndex: 'schid', width: 70 },
      { dataIndex: 'sclinejournal', width: 120 },
      {
        dataIndex: 'debit',
        render: (value, record) =>
          toCurrency(record.debit, localStorage.getItem('currcode')),
        fixed: 'right',
        width:130
      },
      {
        dataIndex: 'credit',
        render: (value, record) =>
          toCurrency(record.credit, localStorage.getItem('currcode')),
        fixed: 'right',
        width:130
      },
    ],
    []
  );

  return (
    (<DF.Provider pagcode="accountingentrymodal">
      <Modal
        title="Detail of Accounting Entry:"
        open={visible}
        onCancel={onCancel}
        footer={null}
        width={1200}
        style={{ top: 20 }}
      >
        <Card>
          {entries ? (
            <UtinaCard comcode="Entries" level={5}>
              <Row gutter={[6, 6]} style={{ marginBottom: '10px' }}>
                <Col xs={24} md={6}>
                  <strong><GetLabel label='Accounting reference:'/></strong>
                </Col>
                <Col xs={24} md={6}>
                  {entries[0]?.accountingreference}
                </Col>
                <Col xs={24} md={6}>
                  <strong><GetLabel label='Total Debit:'/></strong>
                </Col>
                <Col xs={24} md={6}>
                  <Tag color="green">
                    {toCurrency(
                      sumBy(entries, 'debit'),
                      localStorage.getItem('currcode')
                    )}
                  </Tag>
                </Col>
              </Row>
              <Row gutter={[6, 6]} style={{ marginBottom: '10px' }}>
                <Col xs={24} md={6}>
                  <strong><GetLabel label='Date:'/></strong>
                </Col>
                <Col xs={24} md={6}>
                  {entries[0]?.accountingdate.slice(0, 10)}
                </Col>
                <Col xs={24} md={6}>
                  <strong><GetLabel label='Total Credit:'/></strong>
                </Col>
                <Col xs={24} md={6}>
                  <Tag color="green">
                    {toCurrency(
                      sumBy(entries, 'credit'),
                      localStorage.getItem('currcode')
                    )}
                  </Tag>
                </Col>
              </Row>
              <Row gutter={[8, 8]} style={{ marginBottom: '10px' }}>
                <Col xs={24} md={6}>
                  <strong><GetLabel label='Currcode:'/></strong>
                </Col>
                <Col xs={24} md={6}>
                  {localStorage.getItem('currcode')}
                </Col>
                <Col xs={24} md={6}>
                  <strong><GetLabel label='Operation:'/></strong>
                </Col>
                <Col xs={24} md={6}>
                  {entries[0]?.schoperation}
                </Col>
                <Col xs={24} md={6}>
                  <strong><GetLabel label='Audit:'/></strong>
                </Col>
                <Col xs={24} md={6}>
                  {entries[0]?.jobidaudit}
                </Col>
              </Row>
              <Row gutter={[8, 8]} style={{ marginBottom: '10px' }}>
                <Col xs={24} md={6}>
                  <strong><GetLabel label='Object Label:'/></strong>
                </Col>
                <Col xs={24} md={18}>
                  {entries[0]?.objectlabel}
                </Col>
              </Row>
            </UtinaCard>
          ) : null}
          <DataGrid
            rowKey="id"
            dataSource={entries}
            columns={columns}
            isLoading={isFetching}
            onSearch={(value) => {
              setSearchParams(value);
              setsearchs(true);
            }}
            initialSearchValues={searchParams}
            pagination={{ pageSize: 12 }}
          />
        </Card>
      </Modal>
    </DF.Provider>)
  );
};

export default AccountingEntryModal;
